import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "../i18n";

import LoadingSpinner from "./LoadingSpinner";
import { useDispatch, useSelector } from "react-redux";
import {
  getVendorSectorProductsAction,
  getVendorSectorsAction,
} from "../actions/vendorProduct";

const getId = (item) =>
  item?._id || item?.id || item?.sectorId || item?.productId || "";

const getName = (item) => {
  const value =
    item?.name ||
    item?.title ||
    item?.productName ||
    item?.sectorName ||
    "Unnamed";
  if (typeof value === "object" && value !== null) {
    return String(
      value.name ||
        value.title ||
        value.productName ||
        value.sectorName ||
        value.label ||
        value.value ||
        "Unnamed",
    );
  }
  return String(value);
};

const isProductActive = (item) => {
  const nestedProduct =
    (item?.product && typeof item.product === "object" && item.product) ||
    (item?.productId && typeof item.productId === "object" && item.productId) ||
    {};
  const status = [
    item?.status,
    item?.productStatus,
    item?.product_status,
    nestedProduct.status,
    nestedProduct.productStatus,
    nestedProduct.product_status,
  ].find(
    (value) =>
      value !== null &&
      value !== undefined &&
      (typeof value !== "string" || value.trim()),
  );
  const statusValue =
    status && typeof status === "object"
      ? status.value ?? status.label ?? status.name
      : status;

  if (typeof statusValue === "boolean") return statusValue;
  if (typeof statusValue === "number") return statusValue === 1;
  if (statusValue !== null && statusValue !== undefined && String(statusValue).trim()) {
    return ["active", "true", "1"].includes(String(statusValue).trim().toLowerCase());
  }

  const activeFlag =
    item?.isActive ??
    item?.is_active ??
    item?.active ??
    nestedProduct.isActive ??
    nestedProduct.is_active ??
    nestedProduct.active;
  if (activeFlag !== null && activeFlag !== undefined) {
    return (
      activeFlag === true ||
      activeFlag === 1 ||
      activeFlag === "true" ||
      activeFlag === "1"
    );
  }

  // The product list treats records without status metadata as active.
  return true;
};

export default function VendorProductSelector({
  value = [],
  onChange,
  disabled = false,
  productsLabel = "",
  searchable = false,
  required = false,
}) {
  const { t, text } = useTranslation();
  const dispatch = useDispatch();
  const rootRef = useRef(null);
  const [openMenu, setOpenMenu] = useState("");
  const [menuSearch, setMenuSearch] = useState("");
  const [isCustomProductEntryOpen, setIsCustomProductEntryOpen] = useState(false);
  const [customProductDraft, setCustomProductDraft] = useState("");
  const {
    sectors = [],
    sectorsLoading,
    sectorsError,
    sectorProducts = {},
    sectorProductsLoading = {},
    sectorProductsError = {},
  } = useSelector((state) => state.vendorProduct || {});

  const groups = (value.length
    ? value
    : [{ key: "group-empty", sectorId: "", productIds: [] }])
    .slice(0, 1);
  const sectorOptions = useMemo(
    () =>
      sectors
        .map((sector) => ({
          value: String(getId(sector)),
          label: getName(sector),
        }))
        .filter((option) => option.value),
    [sectors],
  );
  const selectedSectorKeys = groups
    .map((group) => group.sectorId)
    .filter(Boolean)
    .join("|");

  useEffect(() => {
    dispatch(getVendorSectorsAction());
  }, [dispatch]);

  useEffect(() => {
    [...new Set(selectedSectorKeys.split("|").filter(Boolean))].forEach(
      (sectorId) => {
        dispatch(getVendorSectorProductsAction(sectorId));
      },
    );
  }, [dispatch, selectedSectorKeys]);

  useEffect(() => {
    if (!openMenu) return undefined;
    const closeOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpenMenu("");
        setIsCustomProductEntryOpen(false);
        setCustomProductDraft("");
      }
    };
    document.addEventListener("mousedown", closeOutside);
    return () => document.removeEventListener("mousedown", closeOutside);
  }, [openMenu]);

  const updateGroup = (key, update) => {
    onChange(
      groups.map((group) =>
        group.key === key ? { ...group, ...update } : group,
      ),
    );
  };

  const selectSector = (group, sectorId) => {
    updateGroup(group.key, { sectorId, productIds: [], customProducts: [] });
    setOpenMenu("");
    setMenuSearch("");
    setIsCustomProductEntryOpen(false);
    setCustomProductDraft("");
  };

  const selectProduct = (group, productId) => {
    updateGroup(group.key, { productIds: [productId], customProducts: [] });
    setOpenMenu("");
    setMenuSearch("");
    setIsCustomProductEntryOpen(false);
    setCustomProductDraft("");
  };

  const selectCustomProduct = (group, productName) => {
    const normalizedName = productName.trim();
    if (!normalizedName) return;
    updateGroup(group.key, { productIds: [], customProducts: [normalizedName] });
    setOpenMenu("");
    setMenuSearch("");
    setIsCustomProductEntryOpen(false);
    setCustomProductDraft("");
  };

  const addCustomProduct = (group) => {
    selectCustomProduct(group, customProductDraft);
  };

  return (
    <div className="vendor-product-selector" ref={rootRef}>
      {groups.map((group, index) => {
        const sectorMenu = "sector-" + group.key;
        const productMenu = "products-" + group.key;
        const products = sectorProducts[group.sectorId] || [];
        const activeProducts = products.filter(isProductActive);
        const chosenSector = sectorOptions.find(
          (option) => option.value === group.sectorId,
        );
        const searchQuery = menuSearch.trim().toLowerCase();
        const visibleSectorOptions = searchable && searchQuery
          ? sectorOptions.filter((option) => option.label.toLowerCase().includes(searchQuery))
          : sectorOptions;
        const visibleProducts = searchable && searchQuery
          ? activeProducts.filter((product) => getName(product).toLowerCase().includes(searchQuery))
          : activeProducts;
        const selectedProductId = (group.productIds || [])[0];
        const selectedProduct = products.find(
          (item) => String(getId(item)) === String(selectedProductId || ""),
        );
        const selectedCustomProduct = selectedProductId
          ? ""
          : (group.customProducts || [])[0] || "";
        const productLabel = selectedProduct
          ? getName(selectedProduct)
          : selectedCustomProduct;

        return (
          <div className="vendor-product-selection-row" key={group.key}>
            <div className="vendor-product-selection-fields">
              <div className="vendor-product-select-field">
                <label htmlFor={"sector-" + group.key}>{t("ui.sector")}{required && <span className="vendor-product-required-mark">*</span>}</label>
                <div className="vendor-product-select">
                  <button
                    id={"sector-" + group.key}
                    type="button"
                    className={
                      "vendor-product-select-trigger" +
                      (chosenSector ? " has-value" : "")
                    }
                    role="combobox"
                    aria-label={t("ui.sectorNumber", { index: index + 1 })}
                    aria-controls={"sector-options-" + group.key}
                    aria-haspopup="listbox"
                    aria-expanded={openMenu === sectorMenu}
                    disabled={disabled || sectorsLoading}
                    onClick={() => {
                      setMenuSearch("");
                      setIsCustomProductEntryOpen(false);
                      setCustomProductDraft("");
                      setOpenMenu(openMenu === sectorMenu ? "" : sectorMenu);
                    }}
                  >
                    <span>
                      {chosenSector?.label ||
                        (sectorsLoading ? (
                          <LoadingSpinner inline size="small" label={t("ui.loadingSectors")} />
                        ) : t("ui.selectSector"))}
                    </span>
                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <path d="m5.5 7.5 4.5 4.5 4.5-4.5" />
                    </svg>
                  </button>
                  {openMenu === sectorMenu && (
                    <div
                      id={"sector-options-" + group.key}
                      className="vendor-product-select-menu"
                      role="listbox"
                      aria-label={t("ui.sectors")}
                    >
                      {searchable && (
                        <div className="vendor-product-select-search">
                          <input
                            type="search"
                            value={menuSearch}
                            placeholder={t("ui.searchSectors")}
                            aria-label={t("ui.searchSectors")}
                            onChange={(event) => setMenuSearch(event.target.value)}
                          />
                        </div>
                      )}
                      {visibleSectorOptions.length ? (
                        visibleSectorOptions.map((option) => (
                          <button
                            type="button"
                            role="option"
                            aria-selected={option.value === group.sectorId}
                            key={option.value}
                            className={
                              "vendor-product-select-option" +
                              (option.value === group.sectorId
                                ? " is-selected"
                                : "")
                            }
                            onClick={() => selectSector(group, option.value)}
                          >
                            <span>{option.label}</span>
                          </button>
                        ))
                      ) : sectorsLoading ? (
                        <LoadingSpinner className="vendor-product-dropdown-loading-state" size="small" label={t("ui.loadingSectors")} />
                      ) : (
                        <div
                          className="vendor-product-select-empty"
                          role="status"
                        >
                          {text(sectorsError || (searchable && searchQuery ? t("ui.noSectorsFoundLabel") : t("ui.noSectorsAvailable")))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="vendor-product-select-field">
                <label htmlFor={"products-" + group.key}>{productsLabel || t("ui.products")}{required && <span className="vendor-product-required-mark">*</span>}</label>
                <div className="vendor-product-select">
                  <button
                    id={"products-" + group.key}
                    type="button"
                    className={
                      "vendor-product-select-trigger" +
                      (productLabel ? " has-value" : "")
                    }
                    role="combobox"
                    aria-label={t("ui.productsForSectorNumber", { index: index + 1 })}
                    aria-controls={"product-options-" + group.key}
                    aria-haspopup="listbox"
                    aria-expanded={openMenu === productMenu}
                    disabled={disabled || !group.sectorId}
                    onClick={() => {
                      setMenuSearch("");
                      setIsCustomProductEntryOpen(false);
                      setCustomProductDraft("");
                      setOpenMenu(openMenu === productMenu ? "" : productMenu);
                    }}
                  >
                    <span>
                      {productLabel ||
                        (group.sectorId
                          ? t("ui.selectProducts")
                          : t("ui.selectSectorFirst"))}
                    </span>
                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <path d="m5.5 7.5 4.5 4.5 4.5-4.5" />
                    </svg>
                  </button>
                  {openMenu === productMenu && (
                    <div
                      id={"product-options-" + group.key}
                      className="vendor-product-select-menu vendor-product-options-menu"
                      role="listbox"
                      aria-label={t("ui.selectProducts")}
                    >
                      {searchable && (
                        <div className="vendor-product-select-search">
                          <input
                            type="search"
                            value={menuSearch}
                            placeholder={t("ui.searchProducts")}
                            aria-label={t("ui.searchProducts")}
                            onChange={(event) => setMenuSearch(event.target.value)}
                          />
                          <small className="vendor-product-custom-product-hint">
                            {t("ui.customProductHowTo")}
                          </small>
                        </div>
                      )}
                      {sectorProductsLoading[group.sectorId] ? (
                        <LoadingSpinner className="vendor-product-dropdown-loading-state" size="small" label={t("ui.loadingProducts")} />
                      ) : sectorProductsError[group.sectorId] ? (
                        <div
                          className="vendor-product-select-empty"
                          role="alert"
                        >
                          {text(sectorProductsError[group.sectorId])}
                        </div>
                      ) : visibleProducts.length ? (
                        visibleProducts.map((product) => {
                          const productId = String(getId(product));
                          const selected = String(selectedProductId || "") === productId;
                          return (
                            <button
                              type="button"
                              role="option"
                              aria-selected={selected}
                              className={
                                "vendor-product-select-option" +
                                (selected ? " is-selected" : "")
                              }
                              key={productId}
                              disabled={disabled}
                              onClick={() => selectProduct(group, productId)}
                            >
                              <span>{getName(product)}</span>
                              {selected && (
                                <span className="vendor-product-option-check" aria-hidden="true">✓</span>
                              )}
                            </button>
                          );
                        })
                      ) : (
                        <div
                          className="vendor-product-select-empty"
                          role="status"
                        >{searchable && searchQuery ? t("ui.noProductsFound") : t("ui.no.products.available.for.this.sector")}</div>
                      )}
                      {selectedCustomProduct && (
                        <button
                          type="button"
                          role="option"
                          aria-selected="true"
                          className="vendor-product-select-option vendor-product-custom-option is-selected"
                          onClick={() => selectCustomProduct(group, selectedCustomProduct)}
                          disabled={disabled}
                        >
                          <span className="vendor-product-custom-badge">{t("ui.custom")}</span>
                          <span className="vendor-product-custom-name">{selectedCustomProduct}</span>
                          <span className="vendor-product-option-check" aria-hidden="true">✓</span>
                        </button>
                      )}
                      <div className="vendor-product-custom-entry-area">
                        <button
                          type="button"
                          role="option"
                          aria-selected="false"
                          aria-expanded={isCustomProductEntryOpen}
                          aria-controls={"custom-product-entry-" + group.key}
                          className="vendor-product-add-custom"
                          onClick={() => {
                            setMenuSearch("");
                            setIsCustomProductEntryOpen((current) => !current);
                          }}
                        >
                          <span className="vendor-product-add-custom-icon" aria-hidden="true">+</span>
                          <span>{t("ui.addCustomProduct")}</span>
                        </button>
                        {isCustomProductEntryOpen && (
                          <div
                            id={"custom-product-entry-" + group.key}
                            className="vendor-product-custom-entry"
                          >
                            <input
                              type="text"
                              value={customProductDraft}
                              placeholder={t("ui.enterCustomProductName")}
                              aria-label={t("ui.enterCustomProductName")}
                              autoFocus
                              onChange={(event) => setCustomProductDraft(event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                  event.preventDefault();
                                  addCustomProduct(group);
                                }
                                if (event.key === "Escape") {
                                  setIsCustomProductEntryOpen(false);
                                  setCustomProductDraft("");
                                }
                              }}
                            />
                            <div className="vendor-product-custom-entry-actions">
                              <button
                                type="button"
                                className="vendor-product-custom-cancel"
                                onClick={() => {
                                  setIsCustomProductEntryOpen(false);
                                  setCustomProductDraft("");
                                }}
                              >
                                {t("ui.cancel")}
                              </button>
                              <button
                                type="button"
                                className="vendor-product-custom-confirm"
                                disabled={!customProductDraft.trim()}
                                onClick={() => addCustomProduct(group)}
                              >
                                {customProductDraft.trim()
                                  ? t("ui.addCustomProductWithName", { name: customProductDraft.trim() })
                                  : t("ui.addCustomProduct")}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
